import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  TextField,
  IconButton,
  Autocomplete,
  Checkbox,
} from '@mui/material';
import {
  CloudSync,
  CheckCircle,
  Error,
  Cloud,
  Assessment,
  TrendingUp,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import * as api from '../services/api';

interface RisorsaSel {
  id: number;
  nome: string;
  cognome: string;
  id_hrp: number;
}

interface RendicontazioniStats {
  allocazioni_da_hrp: number;
  risorse_con_rendicontazioni: number;
  ultima_sincronizzazione: string;
}

interface LastSync {
  last_sync_timestamp: string;
  formatted: string;
}

interface SyncResult {
  success: boolean;
  message?: string;
  total?: number;
  created?: number;
  updated?: number;
  errors?: any[];
  warnings?: string[];
}

interface PreviewAllocazione {
  risorsa_id: number;
  risorsa_nome: string;
  risorsa_id_hrp: number;
  progetto_id: number;
  progetto_titolo: string;
  progetto_id_hrp: number;
  anno: number;
  mese: number;
  totale_minuti: number;
  percentuale: number;
  over_allocated: boolean;
}

interface PreviewResult {
  total_count: number;
  risorse_count: number;
  allocazioni: PreviewAllocazione[];
  warnings: string[];
  last_sync_timestamp: string;
  note: string | null;
}

const SyncRendicontazioniPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [stats, setStats] = useState<RendicontazioniStats | null>(null);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [lastSync, setLastSync] = useState<LastSync | null>(null);
  const [editingTimestamp, setEditingTimestamp] = useState(false);
  const [editedTimestamp, setEditedTimestamp] = useState('');

  // Selezione risorse
  const [availableRisorse, setAvailableRisorse] = useState<RisorsaSel[]>([]);
  const [selectedRisorse, setSelectedRisorse] = useState<RisorsaSel[]>([]);
  const [loadingRisorse, setLoadingRisorse] = useState(false);

  // Carica statistiche, ultimo sync e risorse all'avvio
  useEffect(() => {
    loadStats();
    loadLastSync();
    loadRisorse();
  }, []);

  const loadRisorse = async () => {
    setLoadingRisorse(true);
    try {
      const all = await api.getRisorse(false);
      const withHrp = (all as any[])
        .filter((r: any) => r.id_hrp != null)
        .map((r: any) => ({ id: r.id, nome: r.nome, cognome: r.cognome, id_hrp: r.id_hrp }))
        .sort((a: RisorsaSel, b: RisorsaSel) => a.cognome.localeCompare(b.cognome) || a.nome.localeCompare(b.nome));
      setAvailableRisorse(withHrp);
      setSelectedRisorse(withHrp); // default: tutte selezionate
    } catch (err) {
      console.error('Errore caricamento risorse:', err);
    } finally {
      setLoadingRisorse(false);
    }
  };

  const allSelected = availableRisorse.length > 0 && selectedRisorse.length === availableRisorse.length;

  const loadStats = async () => {
    try {
      const data = await api.getRendicontazioniStats();
      setStats(data);
    } catch (error: any) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const loadLastSync = async () => {
    try {
      const data = await api.getRendicontazioniLastSync();
      setLastSync(data);
    } catch (error: any) {
      console.error('Errore caricamento last sync:', error);
    }
  };

  const testConnection = async () => {
    setTestingConnection(true);
    setConnectionStatus(null);
    try {
      const data = await api.testRendicontazioniConnection();
      setConnectionStatus({
        success: data.success,
        message: data.message,
      });
    } catch (error: any) {
      setConnectionStatus({
        success: false,
        message: error.response?.data?.error || 'Errore durante il test di connessione',
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const loadPreview = async () => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const risorseIds = allSelected ? undefined : selectedRisorse.map(r => r.id);
      const data = await api.rendicontazioniPreview(risorseIds);
      setPreview(data);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Errore durante il caricamento della preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const startEditTimestamp = () => {
    setEditedTimestamp(lastSync?.last_sync_timestamp || '1980-01-01 00:00:00');
    setEditingTimestamp(true);
  };

  const cancelEditTimestamp = () => {
    setEditingTimestamp(false);
    setEditedTimestamp('');
  };

  const saveTimestamp = async () => {
    if (!editedTimestamp) {
      alert('Inserisci un timestamp valido');
      return;
    }

    try {
      const data = await api.updateRendicontazioniLastSync(editedTimestamp);
      setLastSync({
        last_sync_timestamp: data.last_sync_timestamp,
        formatted: data.formatted
      });
      setEditingTimestamp(false);
      alert(data.message);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Errore durante l\'aggiornamento del timestamp');
    }
  };

  const syncAllRendicontazioni = async () => {
    if (!preview) {
      alert('Carica prima la preview delle rendicontazioni da sincronizzare');
      return;
    }

    const risorseLabel = allSelected
      ? `tutte le ${availableRisorse.length} risorse`
      : `${selectedRisorse.length} risorse selezionate`;
    const confirm = window.confirm(
      `Confermi la sincronizzazione di ${preview.total_count} allocazioni da HRP?\n\n` +
      `Questo aggiornerà le allocazioni per ${risorseLabel}.\n\n` +
      `⚠️ ATTENZIONE: Le rendicontazioni passate SOVRASCRIVERANNO tutte le allocazioni esistenti (anche manuali) per le risorse selezionate.\n` +
      `Solo le allocazioni future (data >= oggi) verranno preservate.`
    );

    if (!confirm) return;

    setLoading(true);
    setSyncResult(null);
    try {
      const risorseIds = allSelected ? undefined : selectedRisorse.map(r => r.id);
      const data = await api.syncAllRendicontazioni(risorseIds);
      setSyncResult(data);
      await loadStats();
      await loadLastSync();
      setPreview(null); // Reset preview dopo sync
    } catch (error: any) {
      setSyncResult({
        success: false,
        message: error.response?.data?.error || 'Errore durante la sincronizzazione',
      });
    } finally {
      setLoading(false);
    }
  };

  const getMeseNome = (mese: number): string => {
    const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
    return mesi[mese - 1] || mese.toString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp /> Sincronizzazione Rendicontazioni HRP
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sincronizza le rendicontazioni da HRP (v_attivita_in_agenda) verso HarpaPlanner come allocazioni mensili
        </Typography>
      </Box>

      {/* Statistiche */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Allocazioni da HRP
              </Typography>
              <Typography variant="h4" color="primary">
                {stats?.allocazioni_da_hrp || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Allocazioni con sorgente = 'H'
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                Risorse con Rendicontazioni
              </Typography>
              <Typography variant="h4" color="secondary">
                {stats?.risorse_con_rendicontazioni || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Risorse con almeno un'allocazione da HRP
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography color="text.secondary" variant="body2">
                  Ultimo Sync Rendicontazioni
                </Typography>
                {!editingTimestamp && (
                  <IconButton size="small" onClick={startEditTimestamp} title="Modifica data sync">
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              {editingTimestamp ? (
                <Box>
                  <TextField
                    size="small"
                    fullWidth
                    value={editedTimestamp}
                    onChange={(e) => setEditedTimestamp(e.target.value)}
                    placeholder="YYYY-MM-DD HH:MM:SS"
                    helperText="Formato: YYYY-MM-DD HH:MM:SS"
                    sx={{ mb: 1 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={saveTimestamp}
                    >
                      Salva
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={cancelEditTimestamp}
                    >
                      Annulla
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography variant="h6">
                    {lastSync?.formatted || 'Mai sincronizzato'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Prossimo sync: rendicontazioni dopo questa data
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Test Connessione */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cloud /> Test Connessione HRP
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Verifica la connessione al database HRP per leggere v_attivita_in_agenda
        </Typography>
        <Button
          variant="contained"
          onClick={testConnection}
          disabled={testingConnection}
          startIcon={testingConnection ? <CircularProgress size={20} /> : <Cloud />}
        >
          {testingConnection ? 'Testing...' : 'Test Connessione'}
        </Button>

        {connectionStatus && (
          <Alert severity={connectionStatus.success ? 'success' : 'error'} sx={{ mt: 2 }}>
            {connectionStatus.message}
          </Alert>
        )}
      </Paper>

      {/* Selettore Risorse */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon /> Risorse da Sincronizzare
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona le risorse da includere nella preview e nella sincronizzazione. Per default sono selezionate tutte le risorse con ID HRP valorizzato.
        </Typography>

        {loadingRisorse ? (
          <CircularProgress size={24} />
        ) : (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <Chip
                label={`${selectedRisorse.length} / ${availableRisorse.length} risorse selezionate`}
                color={allSelected ? 'success' : 'warning'}
                size="small"
              />
              {!allSelected && (
                <Button size="small" onClick={() => setSelectedRisorse(availableRisorse)}>
                  Seleziona tutte
                </Button>
              )}
              {selectedRisorse.length > 0 && (
                <Button size="small" color="inherit" onClick={() => setSelectedRisorse([])}>
                  Deseleziona tutte
                </Button>
              )}
            </Box>
            <Autocomplete
              multiple
              options={availableRisorse}
              value={selectedRisorse}
              onChange={(_, newValue) => {
                setSelectedRisorse(newValue);
                setPreview(null); // reset preview se cambia selezione
              }}
              getOptionLabel={(r) => `${r.cognome} ${r.nome} (HRP: ${r.id_hrp})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              disableCloseOnSelect
              limitTags={6}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.cognome} {option.nome}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    HRP: {option.id_hrp}
                  </Typography>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={selectedRisorse.length === 0 ? 'Nessuna risorsa selezionata' : 'Cerca risorsa...'}
                />
              )}
            />
          </Box>
        )}
      </Paper>

      {/* Sincronizza Tutte le Rendicontazioni */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CloudSync /> Sincronizza Tutte le Rendicontazioni
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Sincronizza tutte le rendicontazioni da HRP per risorse con id_hrp valorizzato.
          La sincronizzazione è incrementale: verranno elaborate solo le rendicontazioni con data_inizio dopo l'ultimo sync.
        </Typography>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Importante - Logica di Sovrascrittura:</strong>
          <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
            <li><strong>PASSATO (data {'<'} oggi):</strong> Le rendicontazioni HRP <strong>sovrascrivono completamente</strong> tutte le allocazioni esistenti (anche manuali)</li>
            <li><strong>PRESENTE/FUTURO (data {'>='} oggi):</strong> Le allocazioni pianificate vengono preservate</li>
            <li>Calcolo: 1 FTE/mese = 22 giorni × 8 ore = 176 ore = 10560 minuti (configurabile via HH_FTE)</li>
            <li>Over-allocation {'>'} 100% viene tracciata e segnalata</li>
          </ul>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={loadPreview}
            disabled={loadingPreview || loading}
            startIcon={loadingPreview ? <CircularProgress size={20} /> : <Assessment />}
          >
            {loadingPreview ? 'Caricamento...' : 'Carica Preview'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={syncAllRendicontazioni}
            disabled={loading || !preview || selectedRisorse.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : <CloudSync />}
          >
            {loading ? 'Sincronizzazione...' : allSelected ? 'Sincronizza Tutte' : `Sincronizza ${selectedRisorse.length} risorse`}
          </Button>
        </Box>

        {lastSync && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Sincronizzazione Incrementale Attiva:</strong> Verranno sincronizzate solo le rendicontazioni con data_inizio dopo il <strong>{lastSync.formatted}</strong>
          </Alert>
        )}

        {/* Preview Table */}
        {preview && (
          <Box sx={{ mt: 3 }}>
            <Alert severity={preview.warnings.length > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
              <strong>Trovate {preview.total_count} allocazioni</strong> per {preview.risorse_count} risorse
              {preview.note && ` - ${preview.note}`}
              {preview.last_sync_timestamp && ` - Rendicontazioni dopo: ${new Date(preview.last_sync_timestamp).toLocaleString('it-IT')}`}
            </Alert>

            {preview.warnings.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom><strong>Attenzione:</strong></Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {preview.warnings.slice(0, 10).map((w, idx) => (
                    <li key={idx}><Typography variant="caption">{w}</Typography></li>
                  ))}
                  {preview.warnings.length > 10 && (
                    <li><Typography variant="caption">...e altri {preview.warnings.length - 10} warning</Typography></li>
                  )}
                </Box>
              </Alert>
            )}

            <Box sx={{ maxHeight: 500, overflow: 'auto', border: '1px solid #ddd', borderRadius: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Risorsa</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Progetto</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Periodo</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>Minuti</th>
                    <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #ddd' }}>%</th>
                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.allocazioni.map((alloc, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee', backgroundColor: alloc.over_allocated ? '#fff3cd' : 'transparent' }}>
                      <td style={{ padding: '8px' }}>
                        <Typography variant="body2">{alloc.risorsa_nome}</Typography>
                        <Typography variant="caption" color="text.secondary">ID HRP: {alloc.risorsa_id_hrp}</Typography>
                      </td>
                      <td style={{ padding: '8px' }}>
                        <Typography variant="body2">{alloc.progetto_titolo}</Typography>
                        <Typography variant="caption" color="text.secondary">ID HRP: {alloc.progetto_id_hrp}</Typography>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        <Typography variant="body2">{getMeseNome(alloc.mese)} {alloc.anno}</Typography>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <Typography variant="body2">{alloc.totale_minuti}</Typography>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'right' }}>
                        <Typography variant="body2" fontWeight={alloc.over_allocated ? 'bold' : 'normal'} color={alloc.over_allocated ? 'error' : 'inherit'}>
                          {alloc.percentuale}%
                        </Typography>
                      </td>
                      <td style={{ padding: '8px', textAlign: 'center' }}>
                        {alloc.over_allocated ? (
                          <Chip label="OVER 100%" color="error" size="small" />
                        ) : (
                          <Chip label="OK" color="success" size="small" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Risultato Sincronizzazione */}
      {syncResult && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Risultato Sincronizzazione
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {syncResult.success ? (
            <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 2 }}>
              {syncResult.message || 'Sincronizzazione completata con successo'}
            </Alert>
          ) : (
            <Alert severity="error" icon={<Error />} sx={{ mb: 2 }}>
              {syncResult.message || 'Errore durante la sincronizzazione'}
            </Alert>
          )}

          {syncResult.total !== undefined && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Totale allocazioni elaborate:</strong> {syncResult.total}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Create:</strong> {syncResult.created || 0}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Aggiornate:</strong> {syncResult.updated || 0}
              </Typography>
            </Box>
          )}

          {syncResult.warnings && syncResult.warnings.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="warning.main" gutterBottom>
                <strong>Avvisi ({syncResult.warnings.length}):</strong>
              </Typography>
              <Box component="ul" sx={{ mt: 1, fontSize: 12, maxHeight: 200, overflow: 'auto' }}>
                {syncResult.warnings.map((w, idx) => (
                  <li key={idx}>{w}</li>
                ))}
              </Box>
            </Box>
          )}

          {syncResult.errors && syncResult.errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="error" gutterBottom>
                <strong>Errori riscontrati ({syncResult.errors.length}):</strong>
              </Typography>
              <Box component="pre" sx={{ fontSize: 11, overflow: 'auto', maxHeight: 200, backgroundColor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                {JSON.stringify(syncResult.errors, null, 2)}
              </Box>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default SyncRendicontazioniPage;
